'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArchive, faTrash, faBell } from '@fortawesome/pro-regular-svg-icons';
import { useGetNotificationsQuery, useBulkActionMutation, type Notification } from 'src/api/redux';
import {
  Checkbox,
  EmptyState,
  PulseIconButton,
  PulseButton,
} from 'src/components/PulseUI';

import { InboxItem } from './components';

type StatusFilter = 'all' | 'unread';

interface NotificationsInboxProps {
  onSelect?: (notification: Notification) => void;
  selectedId?: number | string | null;
}

const NotificationsInbox = ({ onSelect, selectedId }: NotificationsInboxProps) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(
    new Set()
  );
  const { data: notifications = [], isLoading } = useGetNotificationsQuery(
    statusFilter === 'unread' ? { status: 'unread' } : undefined
  );
  const [bulkAction] = useBulkActionMutation();

  const hasSelection = selectedIds.size > 0;
  const allSelected =
    notifications.length > 0 && selectedIds.size === notifications.length;

  const handleMarkAllRead = () => {
    bulkAction({ action: 'mark-read' });
  };

  const handleRowClick = (notification: Notification) => {
    if (hasSelection) {
      toggleSelection(notification.id);
    } else {
      onSelect?.(notification);
    }
  };

  const toggleSelection = (id: number | string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map((n) => n.id)));
    }
  };

  const handleBulkArchive = () => {
    bulkAction({ action: 'archive', ids: Array.from(selectedIds) as number[] });
    setSelectedIds(new Set());
  };

  const handleBulkDelete = () => {
    bulkAction({ action: 'delete', ids: Array.from(selectedIds) as number[] });
    setSelectedIds(new Set());
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-3 overflow-hidden rounded-xl border border-neutral-200 bg-white p-4">
        <div className="h-8 w-48 animate-pulse rounded-lg bg-neutral-100" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-lg bg-neutral-100"
          />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="notifications-inbox" className="flex flex-1 flex-col overflow-hidden rounded-xl bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
        <h2 className="text-base font-semibold text-neutral-900">
          Notifications
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <PulseButton
              size="xs"
              variant="plain"
              className={` transition-colors ${
                statusFilter === 'all'
                  ? 'bg-sky-100 text-sky-700'
                  : 'text-neutral-500 bg-neutral-50 hover:bg-neutral-100'
              }`}
              onClick={() => setStatusFilter('all')}
              aria-pressed={statusFilter === 'all'}
              data-testid="filter-all"
            >
              All
            </PulseButton>
            <PulseButton
              size="xs"
              variant="plain"
              className={` transition-colors ${
                statusFilter === 'unread'
                  ? 'bg-sky-100 text-sky-700'
                  : 'text-neutral-500 bg-neutral-50 hover:bg-neutral-100'
              }`}
              onClick={() => setStatusFilter('unread')}
              aria-pressed={statusFilter === 'unread'}
              data-testid="filter-unread"
            >
              Unread
            </PulseButton>
          </div>
          <button
            type="button"
            className="text-xs font-medium text-sky-600 hover:text-sky-700"
            onClick={handleMarkAllRead}
            data-testid="mark-all-read"
          >
            Mark all as read
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-2">
        <Checkbox
          checked={allSelected}
          onChange={toggleSelectAll}
          size="sm"
          label={hasSelection ? `${selectedIds.size} selected` : 'Select all'}
          aria-label="Select all notifications"
          data-testid="select-all"
        />
        <div className="flex items-center gap-1">
          <PulseIconButton
            icon={faArchive}
            variant="plain"
            color="neutral"
            size="sm"
            onClick={handleBulkArchive}
            disabled={!hasSelection}
            tooltip="Archive"
            aria-label="Archive selected"
            data-testid="bulk-archive"
          />
          <PulseIconButton
            icon={faTrash}
            variant="plain"
            color="danger"
            size="sm"
            onClick={handleBulkDelete}
            disabled={!hasSelection}
            tooltip="Delete"
            aria-label="Delete selected"
            data-testid="bulk-delete"
          />
        </div>
      </div>

      {/* List */}
      <div role="list" aria-label="Notifications" data-testid="notifications-list" className="flex flex-1 flex-col overflow-y-auto">
        {!notifications?.length ? (
          <EmptyState
            icon={<Icon icon={faBell} className="text-6xl text-neutral-400" />}
            title="No notifications"
            description="You're all caught up!"
            className="flex-1 items-center justify-center !border-none !shadow-none"
          />
        ) : (
          notifications.map((notification) => (
            <InboxItem
              key={notification.id}
              item={notification}
              isChecked={selectedIds.has(notification.id)}
              isSelected={notification.id === selectedId}
              onClick={() => handleRowClick(notification)}
              onToggle={() => toggleSelection(notification.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsInbox;
